import React, { useContext, useMemo } from "react";
import {
  IconButton,
  ActionsMenu,
  ModalContext,
  type ModalContextType,
} from "@SeemplicityDev/shared";
import ManageDomainUsers from "./ManageDomainUsers/ManageDomainUsers";
import CustomerDataSources from "./CustomerDataSources/CustomerDataSources";
import DeleteAccountModal from "./DeleteAccountModal/DeleteAccountModal";
import DisableAccountModal from "./DisableAccountModal/DisableAccountModal";
import LaunchTask from "./LaunchTask/LaunchTask";
import Actions from "./Actions/Actions";
import AccountConfigsModal from "./AccountConfigs/AccountConfigsModal";

interface AccountActionsPopoverProps {
  accountName: string;
  accountUuid: string;
  organizationName: string;
  domains: string[];
  ssoEnforced?: boolean;
  accountEnabled: boolean;
  // Dead prop: AccountsTable still passes openModal, but the component reads
  // openModal from ModalContext. Typed optional to keep the JSX element identical.
  openModal?: ModalContextType["openModal"];
}

const AccountActionsPopover: React.FC<AccountActionsPopoverProps> = ({
  accountName,
  accountUuid,
  organizationName,
  domains,
  ssoEnforced,
  accountEnabled,
}) => {
  const { openModal } = useContext(ModalContext);

  const menuItems = useMemo(() => {
    const deleteItem = {
      component: (
        <span style={{ color: "var(--icon-danger-button-color)" }}>
          Delete Account
        </span>
      ),
      onClick: () => {
        openModal({
          title: "Delete Account",
          size: "s",
          overlay: true,
          component: (
            <DeleteAccountModal
              accountUuid={accountUuid}
              accountName={accountName}
              organizationName={organizationName}
            />
          ),
        });
      },
    };

    if (!accountEnabled) {
      return [deleteItem];
    }

    const items = [
      {
        component: <span>Actions</span>,
        onClick: () => {
          openModal({
            title: `${accountName} - Actions`,
            size: "xl",
            overlay: true,
            component: (
              <Actions accountName={accountName} accountUuid={accountUuid} />
            ),
          });
        },
      },
      {
        component: <span>Tasks</span>,
        onClick: () => {
          openModal({
            title: `${accountName} - Launch Task`,
            size: "m",
            overlay: true,
            component: <LaunchTask accountUuid={accountUuid} />,
          });
        },
      },
      {
        component: <span>Configurations</span>,
        onClick: () => {
          openModal({
            title: `${accountName} - Configurations`,
            size: "xl",
            overlay: true,
            component: (
              <AccountConfigsModal
                accountUuid={accountUuid}
                accountName={accountName}
              />
            ),
          });
        },
      },
      {
        component: <span>Users</span>,
        onClick: () => {
          openModal({
            title: `Manage ${accountName}'s Users`,
            size: "xxl",
            overlay: true,
            component: (
              <ManageDomainUsers
                accountUuid={accountUuid}
                domains={domains}
                ssoEnforced={ssoEnforced}
              />
            ),
          });
        },
      },
      {
        component: <span>Data Sources</span>,
        onClick: () => {
          openModal({
            title: `${accountName}'s Data Sources`,
            size: "m",
            overlay: true,
            component: (
              <CustomerDataSources
                accountUuid={accountUuid}
                accountName={accountName}
              />
            ),
          });
        },
      },
      {
        component: (
          <span style={{ color: "var(--icon-danger-button-color)" }}>
            Disable Account
          </span>
        ),
        onClick: () => {
          openModal({
            title: "Disable Account",
            size: "s",
            overlay: true,
            component: (
              <DisableAccountModal
                accountUuid={accountUuid}
                accountName={accountName}
                organizationName={organizationName}
              />
            ),
          });
        },
      },
      deleteItem,
    ];
    return items;
  }, [
    accountName,
    accountUuid,
    domains,
    ssoEnforced,
    openModal,
    accountEnabled,
    organizationName,
  ]);

  return (
    <ActionsMenu
      data-testid="cnc-action-menu"
      className="cnc-action-menu"
      menuItems={menuItems}
      allowPropagation
    >
      <IconButton
        data-testid="cnc-more-actions-menu"
        isIcon="seem-icon-more-actions"
        className="rules-table-actions-button"
      />
    </ActionsMenu>
  );
};

export default AccountActionsPopover;
